import { Dialog, DialogContent, Button, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BannerManager from "Components/AnnouncementBanner/BannerManager";
import { getProfileCompletion } from "Utils/profileCompletion";
import styles from "./ProfileCompletionReminder.module.scss";

const REMINDER_DELAY_MS = 2 * 24 * 60 * 60 * 1000;

const getAvatarSrc = (user) =>
  String(
    user?.avatarURL ||
      user?.avatarUrl ||
      user?.profilePicture ||
      user?.picture ||
      "",
  ).trim();

const getInitial = (user) =>
  String(user?.firstName || user?.username || user?.email || "K")
    .trim()
    .charAt(0)
    .toUpperCase() || "K";

const hasValueForReminder = (user) =>
  Boolean(
    user &&
    typeof user === "object" &&
    (user.id || user.uuid || user.email || user.username),
  );

const isRegistrationComplete = (user) =>
  user?.registrationCompleted === true ||
  String(user?.registrationCompleted).toLowerCase() === "true";

const PROFILE_COMPLETION_BANNER_ID = "profile-completion";

const shouldShowReminder = (user, completion) =>
  hasValueForReminder(user) &&
  isRegistrationComplete(user) &&
  completion.percent < 100;

/** Banner message for the shared announcement slot, or null when complete. */
export const getProfileCompletionBannerMessage = (user, onComplete) => {
  const completion = getProfileCompletion(user);
  if (!shouldShowReminder(user, completion)) return null;

  return {
    id: PROFILE_COMPLETION_BANNER_ID,
    priority: 10,
    type: "info",
    text: (
      <>
        <strong>Your profile is {completion.percent}% completed.</strong>{" "}
        Get a more personalised Kewordal experience by completing.
      </>
    ),
    cta: { label: "Complete Now", onClick: onComplete },
    dismissible: false,
  };
};

const ProfileCompletionReminder = ({
  user,
  enabled = true,
  showBanner = true,
  showModal = false,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const completion = useMemo(() => getProfileCompletion(user), [user]);
  const [modalOpen, setModalOpen] = useState(false);
  const avatarSrc = getAvatarSrc(user);
  const shouldShow = enabled && shouldShowReminder(user, completion);

  const reminderKey = `profileCompletionReminder:${user?.id || user?.uuid || user?.email || user?.username}`;

  useEffect(() => {
    if (!shouldShow || !showModal) return;

    let remindAt = 0;
    try {
      const storedTime = Number(localStorage.getItem(reminderKey));
      if (Number.isFinite(storedTime)) remindAt = storedTime;
    } catch (error) {
      console.warn("Could not read profile reminder preference", error);
    }

    const delay = remindAt - Date.now();
    const timer = window.setTimeout(
      () => setModalOpen(true),
      Math.max(0, Math.min(delay, REMINDER_DELAY_MS)),
    );
    return () => window.clearTimeout(timer);
  }, [pathname, reminderKey, shouldShow, showModal]);

  if (!shouldShow) return null;

  const openProfileEditor = () => {
    setModalOpen(false);
    navigate("/profile/edit");
  };

  const handleRemindLater = () => {
    try {
      localStorage.setItem(reminderKey, String(Date.now() + REMINDER_DELAY_MS));
    } catch (error) {
      console.warn("Could not save profile reminder preference", error);
    }
    setModalOpen(false);
  };

  return (
    <>
      {showBanner ? (
        <BannerManager
          messages={[getProfileCompletionBannerMessage(user, openProfileEditor)]}
        />
      ) : null}

      <Dialog
        open={modalOpen && showModal}
        onClose={handleRemindLater}
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.5) !important",
            backdropFilter: "none !important",
            WebkitBackdropFilter: "none !important",
          },
          "& .MuiDialog-container": {
            alignItems: "flex-start",
            justifyContent: "flex-end",
            padding: { xs: "78px 16px 16px", sm: "78px 32px 24px" },
            boxSizing: "border-box",
          },
        }}
        PaperProps={{ className: styles.paper }}
      >
        <DialogContent className={styles.content}>
          <div className={styles.avatarWrap}>
            <div className={styles.avatarInner}>
              {avatarSrc ? <img src={avatarSrc} alt="" /> : getInitial(user)}
            </div>
          </div>

          <Typography className={styles.title}>
            Your profile is {completion.percent}% completed
          </Typography>
          <Typography className={styles.copy}>
            Add a few more details to get a more personalised Kewordal
            experience.
          </Typography>

          <div className={styles.actions}>
            <Button
              variant="outlined"
              className={styles.later}
              onClick={handleRemindLater}
            >
              Remind Me Later
            </Button>
            <Button
              variant="contained"
              className={styles.complete}
              onClick={openProfileEditor}
            >
              Complete Profile
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProfileCompletionReminder;
